import { store } from "../../../index";
import _ from "lodash";

// ─── Wix Stores Catalog V3 mappers ──────────────────────────────────────────────
// V3 counterpart of `wix.js`. Produces the SAME mapped shape the editor UI consumes,
// so the ProductPicker/CategoryPicker and previewer sections work on V3 sites. Key V3
// differences: price on the variant (`actualPrice`/`compareAtPrice`); `manageVariants`
// removed (options always create variants); Query/Search return no variants (only Get
// Product does); media at `media.itemsInfo.items[]`; choice media at
// `options[].choicesSettings.choices[].linkedMedia[]`.

const toNumber = (amount) => {
  const n = parseFloat(amount);
  return isNaN(n) ? 0 : n;
};

const mapV3Media = (mediaItems = []) =>
  mediaItems.reduce((result, element) => {
    if (element?.image?.url) {
      result.push({ imageUrl: element.image.url });
    } else if (element?.video?.resolutions?.[0]?.url) {
      result.push({ videoThumbnail: true, imageUrl: element.video.resolutions[0].url });
    }
    return result;
  }, []);

const getImagesFromChoicesV3 = (product, choices) => {
  let images = [];
  for (const [optionName, choiceName] of Object.entries(choices)) {
    const option = (product.options || []).find((o) => o.name === optionName);
    const choice = _.get(option, 'choicesSettings.choices', []).find((c) => c.name === choiceName);
    for (const linked of _.get(choice, 'linkedMedia', [])) {
      if (linked?.image?.url) images.push({ imageUrl: linked.image.url });
    }
  }
  return images;
};

export const mapWixV3Categories = (categories, categoryIdsToHide = []) => {
  const categoryOrder = _.get(
    store.getState().SettingsReducer,
    'generalSettings.categoryOrder',
    'asc',
  );

  let mapped = (categories || []).reduce((result, category) => {
    if (category.visible !== false && !categoryIdsToHide.includes(category.id)) {
      result.push({
        id: category.id,
        name: _.get(category, 'name', ''),
        parentId: _.get(category, 'parentCategory.id', ''),
        breadcrumb: _.get(category, 'name', ''),
        subcategories: [],
      });
    }
    return result;
  }, []);

  if (categoryOrder === 'desc') {
    mapped.sort((a, b) => b.name.localeCompare(a.name));
  } else {
    mapped.sort((a, b) => a.name.localeCompare(b.name));
  }

  return mapped;
};

export const mapWixV3Product = (product) => {
  let mappedProduct = {};

  mappedProduct["id"] = product.id;
  mappedProduct["name"] = _.get(product, 'name', '');
  mappedProduct["brand"] = _.get(product, 'brand.name', '');
  mappedProduct["description"] = _.get(product, 'plainDescription', '').replace(/[\n\r]/g, "");
  mappedProduct["url"] = _.get(product, 'url.url', '');
  mappedProduct["manageVariants"] = (product.options || []).length > 0;

  mappedProduct["additionalInfo"] = _.get(product, 'infoSections', []);

  const actualMin = toNumber(_.get(product, 'actualPriceRange.minValue.amount'));
  const compareMin = toNumber(_.get(product, 'compareAtPriceRange.minValue.amount'));
  const hasWixPromotinalPrice = compareMin > 0 && actualMin > 0 && compareMin > actualMin;

  mappedProduct["price"] = actualMin;
  mappedProduct["oldPrice"] = hasWixPromotinalPrice ? compareMin : 0;
  mappedProduct["displayPrice"] = _.get(product, 'actualPriceRange.minValue.formattedAmount', '');
  mappedProduct["displayOldPrice"] = _.get(
    product,
    'compareAtPriceRange.minValue.formattedAmount',
    '',
  );

  if (hasWixPromotinalPrice && compareMin > 0) {
    mappedProduct["discountPercentage"] = (((compareMin - actualMin) / compareMin) * 100).toFixed(0);
  }

  mappedProduct["hasPromotionalPrice"] = hasWixPromotinalPrice;
  mappedProduct["hasVariant"] = (product.options || []).length > 0;

  const showPricesOnlyToLoggedUsers = _.get(
    store.getState().SettingsReducer,
    'generalSettings.showPricesOnlyToLoggedUsers',
    false,
  );
  mappedProduct["canShowPrice"] = () =>
    !showPricesOnlyToLoggedUsers ||
    (showPricesOnlyToLoggedUsers && !!store.getState().CustomersReducer.loggedUserId);

  const availabilityStatus = _.get(product, 'inventory.availabilityStatus');
  mappedProduct["stock"] = availabilityStatus === 'OUT_OF_STOCK' ? 0 : 999;

  const freeTextModifiers = (product.modifiers || []).filter(
    (m) => _.get(m, 'modifierRenderType') === 'FREE_TEXT' || _.get(m, 'freeTextSettings'),
  );
  mappedProduct["isCustomizable"] = freeTextModifiers.length > 0;
  if (mappedProduct["isCustomizable"]) {
    mappedProduct["customizableFields"] = freeTextModifiers.map((m) => ({
      Name: _.get(m, 'freeTextSettings.title', _.get(m, 'name', '')),
      Type: 'text',
      Value: 0,
      DisplayValue: "R$ 0,00",
      Mandatory: _.get(m, 'mandatory', false),
    }));
  }

  mappedProduct["images"] = mapV3Media(_.get(product, 'media.itemsInfo.items', []));

  return mappedProduct;
};

export const mapWixV3Variants = (product) => {
  const indexByAvailableVariant = _.get(
    store.getState().SettingsReducer,
    'generalSettings.indexByAvailableVariant',
    false,
  );

  const variants = _.get(product, 'variantsInfo.variants', []).filter((v) => v.visible !== false);

  return variants.reduce((mapped, item) => {
    const actual = toNumber(_.get(item, 'price.actualPrice.amount'));
    const compareAt = toNumber(_.get(item, 'price.compareAtPrice.amount'));
    const hasWixPromotinalPrice = compareAt > 0 && actual > 0 && compareAt > actual;

    let skuList = {};
    const choices = {};
    for (const choice of _.get(item, 'choices', [])) {
      const name = _.get(choice, 'optionChoiceNames.optionName');
      const value = _.get(choice, 'optionChoiceNames.choiceName');
      if (name) {
        skuList[name] = value;
        choices[name] = value;
      }
    }

    const isAvailable = _.get(item, 'inventoryStatus.inStock', true);
    const stock = isAvailable ? 999 : 0;

    if (indexByAvailableVariant && !isAvailable) return mapped;

    let images = getImagesFromChoicesV3(product, choices);
    if (_.isEmpty(images)) {
      images = mapV3Media(_.get(product, 'media.itemsInfo.items', []));
    }

    mapped.push({
      Sku: skuList,
      Price: actual,
      DisplayPrice: _.get(item, 'price.actualPrice.formattedAmount', ''),
      OldPrice: hasWixPromotinalPrice ? compareAt : 0,
      DisplayOldPrice: _.get(item, 'price.compareAtPrice.formattedAmount', ''),
      HasPromotionalPrice: hasWixPromotinalPrice,
      Id: item.id,
      Images: images,
      IsAvailable: isAvailable,
      Stock: stock,
      StockTrack: false,
    });
    return mapped;
  }, []);
};

export const mapWixV3VariantOptions = (product) => {
  let mappedVariantOptions = {};

  for (const option of product.options || []) {
    const name = _.get(option, 'name');
    if (_.isEmpty(name) || mappedVariantOptions[name] !== undefined) continue;

    mappedVariantOptions[name] = _.get(option, 'choicesSettings.choices', [])
      .filter((c) => c.visible !== false)
      .map((c) => ({ value: _.get(c, 'name', '') }));
  }

  return mappedVariantOptions;
};

export const mapWixV3ProductWithVariants = (product) => {
  let mappedProduct = mapWixV3Product(product);
  let mappedVariants = mapWixV3Variants(product);

  mappedProduct["variants"] = mappedVariants;
  mappedProduct["hasDifferentVariantPrices"] = false;

  let previousVariantPrice = null;
  mappedVariants.forEach((variant) => {
    if (variant.IsAvailable) {
      if (mappedProduct["price"] > variant.Price || mappedProduct["price"] === 0) {
        mappedProduct["price"] = variant.Price;
        mappedProduct["displayPrice"] = variant.DisplayPrice;
      }
    }
    if (!!previousVariantPrice && previousVariantPrice !== variant.Price) {
      mappedProduct["hasDifferentVariantPrices"] = true;
    }
    previousVariantPrice = variant.Price;
  });

  mappedProduct["isVisible"] = product.visible !== false;
  mappedProduct["isAvailable"] =
    mappedVariants.length > 0
      ? mappedVariants.some((v) => v.IsAvailable)
      : _.get(product, 'inventory.availabilityStatus') !== 'OUT_OF_STOCK';
  mappedProduct["variantOptions"] = mapWixV3VariantOptions(product);

  return mappedProduct;
};

export const mapWixV3ProductArrayWithVariants = (products) => {
  const hideUnavailableProducts = _.get(
    store.getState().SettingsReducer,
    'generalSettings.hideUnavailableProducts',
    false,
  );

  let mapped = (products || []).map((product) => mapWixV3ProductWithVariants(product));
  mapped = mapped.filter((product) => product.isVisible);
  if (hideUnavailableProducts) {
    mapped = mapped.filter((product) => product.isAvailable);
  }
  return mapped;
};
